import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What is a unit in the stock market?',
    a: 'A unit is one tradeable security that bundles several pieces \u2014 almost always one common share plus a warrant and/or a right \u2014 under a single ticker that ends in U (JABRU, CMIIU). You buy and sell the bundle for one price until the pieces are allowed to separate, after which the share, warrant and right can each trade under their own symbol.',
  },
  {
    q: 'Is a ticker ending in U always a SPAC?',
    a: 'No, but almost every five-letter US ticker ending in U is a unit, and the great majority of units are SPAC units. A handful of young operating companies (biotech, early-stage tech) also IPO with units to sweeten the deal. The badge in DiviDrip tells you which: SPAC units show both the UNIT and SPAC badges.',
  },
  {
    q: 'Is JABRU a warrant?',
    a: 'No. JABRU is the unit of JAB Acquisition Corp I. Each unit bundles one Class A share, one warrant and one right. The warrant alone trades as JABRW and the right as JABRR; the share trades under its own symbol. A unit is never a warrant \u2014 it contains one.',
  },
  {
    q: 'When can I split a unit into its share and warrant?',
    a: 'Typically 52 days after the IPO prospectus date (sometimes 45). The separation date is in the prospectus and announced by press release. Splitting is voluntary: you ask your broker to submit a voluntary corporate-action request. Many brokers charge a flat fee for this (commonly in the $30\u201350 range, though some waive it), and fractional warrants are rounded down, so split in multiples that yield whole warrants.',
  },
  {
    q: 'What is a SPAC right and how does it convert?',
    a: 'A right is a promise to receive a fraction of a share \u2014 one-tenth is the most common ratio \u2014 automatically when the SPAC closes its business combination. There is nothing to exercise or pay. Ten rights at 1/10 become one share; leftover fractions are usually rounded down or cashed out. If the SPAC liquidates instead of merging, rights expire worthless.',
  },
  {
    q: 'What happens to a unit if the SPAC never finds a deal?',
    a: 'The SPAC liquidates and returns the trust account (roughly $10 per share plus interest) to shareholders. The share inside your unit gets that cash. The warrant and the right inside the same unit get nothing \u2014 they expire worthless. That is the key difference between the share and the sweeteners.',
  },
  {
    q: 'Do units, warrants or rights pay dividends?',
    a: 'No. A SPAC has no business and no earnings, so none of its securities pay anything; the trust interest stays in the trust. Regular-stock units bundle shares of young companies that are reinvesting everything. DiviDrip flags all three as non-dividend and keeps them out of the Dividend screener and the yield math.',
  },
  {
    q: 'How does DiviDrip tell units, warrants and rights apart?',
    a: 'The stock modal header shows a teal UNIT badge, an amber WARRANT badge or a fuchsia RIGHT badge, and the Insights tab opens the matching card: the SPAC Unit card lists the bundled tickers, the Warrant card has the break-even and P/L calculator, the Right card shows the conversion ratio and what happens at close or liquidation. The Warrant Screener lists warrants only; the Stock Screener\u2019s SPAC filter lists every share, unit and warrant of every SPAC.',
  },
];

const SUFFIXES = [
  ['U', 'Unit', 'JABRU', 'Share + warrant and/or right bundled. NYSE uses .U or -UN instead.'],
  ['W or .WS', 'Warrant', 'JABRW, AAC.WS', 'Right to buy one share at a fixed strike (usually $11.50) for about five years after the merger.'],
  ['R', 'Right', 'JABRR, HAVAR', 'Converts automatically into a fraction of a share (commonly 1/10) when the merger closes.'],
  ['F', 'Foreign ordinary', 'LVMHF', 'A foreign company\u2019s home-market share traded over the counter in the US.'],
  ['Y', 'ADR', 'BABAY', 'American Depositary Receipt for a foreign company.'],
];

const COMPARE = [
  ['Purpose', 'Raise a blind pool of cash to find a private company to merge with.', 'Sweeten a risky or young company\u2019s IPO so investors buy in.'],
  ['What is inside', '1 Class A share + a fraction of a warrant and/or a right.', '1 common share + a warrant (rights are rare).'],
  ['Downside protection', 'High. About $10.00 per share is held in a trust account invested in Treasuries.', 'None. Value depends entirely on the business.'],
  ['If things go wrong', 'SPAC liquidates: the share gets the trust cash back; warrants and rights expire worthless.', 'Company fails: the stock can go to zero and the warrants expire worthless.'],
  ['Dividends', 'Never \u2014 there is no business.', 'Almost never \u2014 the company is reinvesting.'],
];

const LIFECYCLE = [
  ['1', 'IPO \u2014 bundled trading', 'Units list at $10.00 under the U ticker. For the first weeks only the bundle trades; you cannot buy the share or warrant alone.'],
  ['2', 'Separation date', 'Usually 52 days after the prospectus date (sometimes 45) the issuer announces that holders may separate their units.'],
  ['3', 'Voluntary split', 'You keep the unit, or you instruct your broker to split it. If split, the unit leaves your account and the share and warrant (and right) appear in its place.'],
  ['4', 'Independent trading', 'Share, warrant and right trade under their own symbols alongside the units nobody split. Prices can drift apart: the sum of the pieces is often a little more or less than the unit.'],
  ['5', 'De-SPAC or liquidation', 'Merger closes: shares become the new company, warrants stay warrants on the new ticker, rights convert to fractional shares. No deal: trust cash goes to the shares; warrants and rights die.'],
];

export default function LearnUnits() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
  };

  return (
    <LearnPage
      title="Units, Warrants & Rights — the U, W and R Tickers Explained | TwylightCrow"
      description="What a stock unit is, how SPAC units differ from regular-stock units, the 52-day separation date, how to split a unit at your broker, what a right converts into at the merger, and how DiviDrip labels each piece."
      canonical="https://twylightcrow.com/learn/units"
      guidePath="/learn/units"
      sectionLabel="Non-Div Basics"
      sectionAnchor="non-div-basics"
      kicker="Learn · Non-Dividend Stocks"
      h1="Units, Warrants & Rights — the U, W and R Tickers Explained"
      datePublished="2026-10-09"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/spac-lifecycle', label: 'The SPAC Lifecycle — trust account, redemption right and the filings that mark each stage' },
        { to: '/warrants', label: 'Warrant Screener — every live US warrant with strike, expiry and break-even' },
        { to: '/screener?filter=all&is_spac=only', label: 'Stock Screener — SPAC filter lists every share, unit and warrant of every blank-check shell' },
        { to: '/learn/how-your-shares-are-held', label: 'Who Actually Holds Your Shares — the DTCC plumbing behind a broker split request' },
        { to: '/learn/lifecycle-of-a-stock', label: 'The Lifecycle of a Stock — where a freshly de-SPACed company lands' },
      ]}
    >
      <blockquote className="border-l-4 border-teal-500/60 pl-4 my-4" data-testid="units-guide-definition">
        <p className="font-semibold text-foreground mb-1">What is a Unit?</p>
        <p>
          A hybrid security that bundles several distinct assets &mdash; typically one share of common
          stock and a warrant or right (often a fraction of one) &mdash; into a single tradeable asset
          under one ticker symbol, which ends in <strong>U</strong>. Think of it as a value meal:
          instead of buying the burger, fries and drink separately, you buy the package for one price.
        </p>
      </blockquote>
      <p>
        If you have ever searched a ticker like <strong>JABRU</strong> and been told it is a
        &ldquo;warrant,&rdquo; this guide is the correction. A unit is never a warrant &mdash; it{' '}
        <em>contains</em> one. The three pieces behave very differently when the company succeeds,
        fails or merges, and the suffix on the ticker tells you which piece you are holding.
      </p>

      <h2 id="suffixes">Reading the fifth letter</h2>
      <p>
        US exchanges use a standard fifth-letter suffix to mark what a symbol represents. For the
        securities in this guide, three letters matter &mdash; and two more show up constantly in the
        same search results.
      </p>
      <table className="learn-table">
        <thead><tr><th>Suffix</th><th>Means</th><th>Examples</th><th>What you own</th></tr></thead>
        <tbody>
          {SUFFIXES.map(([s, m, ex, what]) => (
            <tr key={s}><td className="font-mono font-semibold">{s}</td><td className="font-semibold">{m}</td><td className="font-mono">{ex}</td><td>{what}</td></tr>
          ))}
        </tbody>
      </table>

      <h2 id="lifecycle">How a unit works &mdash; the lifecycle</h2>
      <p>
        Units are almost always born at an IPO and most of them die within a few years, either by
        being split apart or by the issuer completing (or abandoning) its merger. The sequence is the
        same every time.
      </p>
      <table className="learn-table">
        <thead><tr><th>Step</th><th>Stage</th><th>What happens</th></tr></thead>
        <tbody>
          {LIFECYCLE.map(([n, stage, what]) => (
            <tr key={n}><td className="font-mono">{n}</td><td className="font-semibold">{stage}</td><td>{what}</td></tr>
          ))}
        </tbody>
      </table>
      <p>
        A practical consequence of step 4: once the pieces trade separately, the unit&rsquo;s own
        volume dries up. Most of the market moves to the share and warrant symbols, so a unit quote
        can look stale or thin. That is normal, not a sign of trouble.
      </p>

      <h2 id="spac-vs-regular">SPAC units vs regular-stock units</h2>
      <p>
        The structure is identical; the risk is not. In a <strong>SPAC</strong> the company is a pile
        of cash looking for a business, so the share has a floor (the trust) and the warrant or right
        is a bonus for backing the sponsor team. In a <strong>regular operating company</strong> the
        unit exists because the IPO was a hard sell &mdash; there is no trust, and the warrant is
        compensation for taking real business risk. For how the SPAC side plays out after the
        merger, see the{' '}
        <Link to="/learn/spac-lifecycle" className="ht-inline-link">SPAC Lifecycle guide</Link>.
      </p>
      <table className="learn-table">
        <thead><tr><th>Feature</th><th>SPAC unit (e.g. JABRU)</th><th>Regular-stock unit</th></tr></thead>
        <tbody>
          {COMPARE.map(([f, spac, reg]) => (
            <tr key={f}><td className="font-semibold">{f}</td><td>{spac}</td><td>{reg}</td></tr>
          ))}
        </tbody>
      </table>

      <h2 id="the-share">The share: the piece with a floor</h2>
      <p>
        In a SPAC the Class A share is the only piece backed by the trust account. Shareholders get
        two chances to take the trust cash instead of the deal: they can <strong>redeem</strong> at
        the merger vote, and they get it back automatically if the SPAC liquidates. That is why SPAC
        shares spend their whole pre-merger life pinned near $10. After the merger the share simply
        becomes a share of the new operating company, usually under a new ticker.
      </p>

      <h2 id="the-warrant">The warrant: leverage with an expiry</h2>
      <p>
        A SPAC warrant is a long-dated option issued by the company itself: the right to buy one
        share at a fixed <strong>strike</strong>, almost always <strong>$11.50</strong>, for about
        five years after the merger closes. Because a warrant costs a dollar or two while the share
        costs ten, it moves several times as fast in percentage terms &mdash; in both directions. Two
        things kill warrants: the SPAC liquidating (they expire worthless, no trust cash), and the
        issuer <strong>force-redeeming</strong> them, which it can do with 30 days&rsquo; notice once
        the stock trades above a trigger price (typically $18). The{' '}
        <Link to="/warrants" className="ht-inline-link">Warrant Screener</Link> lists every live US
        warrant with its strike, expiry, distance to break-even and any redemption notice our weekly
        8-K watcher has found.
      </p>

      <h2 id="the-right">The right: automatic conversion at the merger</h2>
      <p>
        A right is the least understood piece and the simplest. It is a promise that when the SPAC
        closes its business combination, each right turns into a <strong>fraction of a share</strong>{' '}
        &mdash; one-tenth is by far the most common ratio, though 1/8, 1/15 and 1/20 exist. The
        conversion is <strong>automatic</strong>: there is no strike price, nothing to exercise and
        nothing to pay. Your broker removes the rights and credits the shares on the closing date.
        Ten rights at 1/10 become one share; a leftover fraction is usually rounded down or paid in
        cash, which is why rights are best bought in multiples of the ratio.
      </p>
      <ul>
        <li><strong>Merger closes</strong> &rarr; rights convert. A right is worth roughly the share price &times; the ratio &times; the probability the deal closes. With the share at $10 and a 1/10 ratio, a right trading at $0.60 is pricing about a 60% chance of closing.</li>
        <li><strong>SPAC liquidates</strong> &rarr; rights expire worthless. Unlike the share, a right has no claim on the trust account.</li>
        <li><strong>No vote, no dividend.</strong> Rights carry no voting power and pay nothing. The exact ratio and closing date are in the issuer&rsquo;s 8-K and S-4 filings.</li>
      </ul>
      <p>
        Massive&rsquo;s security names sometimes spell the ratio out &mdash; HAVAR is listed as
        &ldquo;Rights that convert on a 1/10th of 1 basis to Class A ordinary Shares&rdquo; &mdash; and
        DiviDrip&rsquo;s Right card reads that ratio when it is present.
      </p>

      <h2 id="splitting">Splitting a unit at your broker</h2>
      <p>
        Separation does not happen by itself. Three practical details catch people out.
      </p>
      <ol>
        <li><strong>It is a broker request.</strong> You submit a <em>voluntary corporate action</em> with your broker (Fidelity, Schwab, Robinhood and the rest all handle it, usually by phone or secure message). Processing takes a few business days.</li>
        <li><strong>There is often a fee.</strong> Many brokers charge a flat amount per request &mdash; commonly in the $30&ndash;50 range, though some waive it &mdash; and it is the same whether you split 10 units or 10,000. On a small position the fee can exceed the value of the warrants you are freeing.</li>
        <li><strong>Fractions are rounded down.</strong> A unit with one-third of a warrant yields a whole warrant only for every three units. Hold a multiple of the fraction&rsquo;s denominator before you split, or you forfeit the remainder.</li>
      </ol>
      <p>
        The alternative is to do nothing: an unsplit unit still receives everything its pieces would.
        At the merger the share converts, the warrant becomes a warrant on the new company and the
        right converts to shares, all inside the unit position.
      </p>

      <h2 id="dividrip">How DiviDrip shows units, warrants and rights</h2>
      <p>
        The badge in the stock-modal header tells you which piece you are looking at: a teal{' '}
        <strong>UNIT</strong>, an amber <strong>WARRANT</strong> or a fuchsia <strong>RIGHT</strong>,
        next to the SPAC badge when the issuer is a blank-check shell. The Insights tab then opens
        the matching card. The <strong>SPAC Unit</strong> card lists the bundled share, warrant and
        right tickers and links to each one we track; the <strong>Warrant</strong> card carries the
        strike, expiry, break-even and P/L calculator; the <strong>Right</strong> card shows the
        conversion ratio and what happens at close or liquidation. All three are flagged non-dividend,
        so they never leak into yield math or the Dividend screener. The{' '}
        <Link to="/warrants" className="ht-inline-link">Warrant Screener</Link> lists warrants only,
        and the{' '}
        <Link to="/screener?filter=all&is_spac=only" className="ht-inline-link">Stock Screener&rsquo;s SPAC filter</Link>{' '}
        lists every share, unit and warrant of every SPAC so you can see a whole family side by side.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }, i) => (
          <div key={i} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>
    </LearnPage>
  );
}
